import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSurvey } from '@/contexts/SurveyContext';
import { useSurveyGeneration } from '@/hooks/useSurveyGeneration';
import { useSurveyTemplateGeneration } from '@/hooks/useSurveyTemplateGeneration';
import { useSurveyTemplate } from '@/hooks/useSurveyTemplates';
import { useToast } from '@/hooks/use-toast';
import { useCreateGeneration, useUpdateGeneration, useTrackEvent, useGeneration } from '@/hooks/useGenerations';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { AppLayout } from '@/components/layout/AppLayout';
import { AppHeader } from '@/components/layout/AppHeader';
import { Footer } from '@/components/layout/Footer';

// Import refactored components
import ConversationTemplateResults from '@/components/survey/ConversationTemplateResults';
import SurveyMainContent from '@/components/survey/SurveyMainContent';
import LoadingState from '@/components/survey/LoadingState';
import EmptyState from '@/components/survey/EmptyState';
import ContactDialog from '@/components/survey/ContactDialog';
import { isUUID } from '@/lib/slugUtils';

const ToolResults: React.FC = () => {
  const navigate = useNavigate();
  const { toolId: toolParam, generationId: routeGenerationId, lang: routeLang } = useParams<{ toolId: string; generationId?: string; lang?: string }>();
  const currentLang = routeLang || 'en';
  const location = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const { 
    config, 
    setConfig,
    generatedQuestions, 
    setGeneratedQuestions, 
    isGenerating, 
    setIsGenerating 
  } = useSurvey();
  const [tool, setTool] = useState<any>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [conversationTemplate, setConversationTemplate] = useState<any>(null);
  const [isTemplateGeneration, setIsTemplateGeneration] = useState(false);
  const [currentGeneration, setCurrentGeneration] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [pendingAutoGenerate, setPendingAutoGenerate] = useState(false);
  
  const { user } = useAuth();
  const createGeneration = useCreateGeneration();
  const updateGeneration = useUpdateGeneration();
  const trackEvent = useTrackEvent();
  
  const surveyGeneration = useSurveyGeneration();

  // Check if this is a conversation template result, survey template result, or existing generation
  const templateId = new URLSearchParams(location.search).get('templateId');
  const surveyTemplateId = new URLSearchParams(location.search).get('surveyTemplateId');
  const queryGenerationId = new URLSearchParams(location.search).get('generationId');
  const generationId = routeGenerationId || queryGenerationId;
  const mode = new URLSearchParams(location.search).get('mode') || 'view'; // view, edit, answer
  
  // Load existing generation if generationId is provided
  const { data: existingGeneration } = useGeneration(generationId || '');
  
  // Use survey template generation hook
  const { data: templateQuestions, isLoading: isTemplateLoading } = useSurveyTemplateGeneration(surveyTemplateId);
  const { data: surveyTemplate } = useSurveyTemplate(surveyTemplateId || '');

  useEffect(() => {
    const fetchTool = async () => {
      if (!toolParam) return;
      
      try {
        const isId = isUUID(toolParam);
        let query = supabase
          .from('tools')
          .select('id, slug, name, description, icon')
          .eq('is_active', true);
          
        if (isId) {
          query = query.eq('id', toolParam);
        } else {
          query = query.eq('slug', toolParam);
        }
        
        const { data, error } = await query.single();

        if (error) throw error;
        setTool(data);
      } catch (error) {
        console.error('Error fetching tool:', error);
      }
    };

    fetchTool();
  }, [toolParam]);

  useEffect(() => {
    const fetchConversationTemplate = async () => {
      if (!templateId) return;
      
      try {
        const { data, error } = await supabase
          .from('conversation_templates')
          .select(`
            *,
            stages:conversation_stages(
              *,
              questions:conversation_questions(*),
              objectives:conversation_objectives(*)
            )
          `)
          .eq('id', templateId)
          .single();

        if (error) throw error;
        setConversationTemplate(data);
      } catch (error) {
        console.error('Error fetching conversation template:', error);
      }
    };

    fetchConversationTemplate();
  }, [templateId]);

  // Determine if we should auto-generate on load (fresh run without templates or existing generation)
  const shouldAutoGenerate = !templateId 
    && !surveyTemplateId 
    && !generationId 
    && !!config.companySize 
    && !!config.industry 
    && generatedQuestions.length === 0 
    && !hasGenerated;

  useEffect(() => {
    if (!toolParam) return;

    // Handle existing generation loading
    if (generationId && existingGeneration && !hasGenerated) {
      const questions = existingGeneration.generated_content?.questions || [];
      setGeneratedQuestions(questions);
      setCurrentGeneration(existingGeneration);
      setTitle(existingGeneration.title || '');
      setHasGenerated(true);
      setIsEditing(mode === 'edit');
      // Load saved configuration into context for accurate sidebar stats
      if (existingGeneration.configuration) {
        setConfig(existingGeneration.configuration);
      }
      
      // Set public URL if exists
      if (existingGeneration.public_url_slug) {
        // Public URL handling moved to components
      }
      
      // Check ownership for editing
      if (mode === 'edit' && user && existingGeneration.user_id !== user.id) {
        toast({
          title: t('results.accessDeniedTitle'),
          description: t('results.accessDeniedDesc'),
          variant: "destructive",
        });
        navigate(`/${currentLang}/tools/${tool?.slug || tool?.id}/results?generationId=${generationId}&mode=view`);
        return;
      }
      
      toast({
        title: t('results.loadedTitle'),
        description: t('results.loadedDesc', { count: questions.length }),
      });
      return;
    }

    // Handle survey template questions
    if (surveyTemplateId && templateQuestions && templateQuestions.length > 0 && !isTemplateGeneration) {
      setGeneratedQuestions(templateQuestions);
      setIsTemplateGeneration(true);
      toast({
        title: t('results.templateLoadedTitle'),
        description: t('results.templateLoadedDesc', { count: templateQuestions.length }),
      });
      return;
    }

    // If no config is set and no templateId and no surveyTemplateId and no generationId, redirect to configuration
    if ((!config.companySize || !config.industry) && !templateId && !surveyTemplateId && !generationId) {
      // If tool metadata isn't loaded yet, don't redirect to an invalid URL; let the header render.
      if (tool?.slug || tool?.id) {
        navigate(`/${currentLang}/tools/${tool?.slug || tool?.id}/configuration`);
      }
      return;
    }

    // For regular survey generation (not templates or existing generations), generate fresh questions
    if (shouldAutoGenerate) {
      if (tool?.id) {
        generateQuestions();
      } else {
        setPendingAutoGenerate(true);
      }
    }
  }, [toolParam, tool, config.companySize, config.industry, templateId, surveyTemplateId, templateQuestions, isTemplateGeneration, generationId, existingGeneration, hasGenerated, mode, user, shouldAutoGenerate]);

  // If we needed to auto-generate but the tool wasn't ready yet, run once it is
  useEffect(() => {
    if (pendingAutoGenerate && tool?.id) {
      setPendingAutoGenerate(false);
      generateQuestions();
    }
  }, [pendingAutoGenerate, tool]);

  const generateQuestions = async () => {
    if (!tool?.id) {
      // Tool not ready; mark pending and return
      setPendingAutoGenerate(true);
      return;
    }
    setIsGenerating(true);
    setHasGenerated(true);
    setGeneratedQuestions([]); // Clear previous questions
    
    try {
      const questions = await surveyGeneration.mutateAsync(config);
      setGeneratedQuestions(questions);
      
      // Save generation to database (only if user is authenticated)
      if (user && tool?.id) {
        try {
          const generation = await createGeneration.mutateAsync({
            tool_id: tool.id,
            user_id: user.id,
            title: `${config.industry} Survey - ${config.companySize}`,
            generation_type: 'survey',
            generated_content: { questions },
            configuration: config,
            estimated_duration_minutes: Math.ceil(questions.length * 1.5), // ~1.5 min per question
            status: 'active'
          });
          setCurrentGeneration(generation);
          // Ensure URL contains generationId so refresh preserves state
          const params = new URLSearchParams(location.search);
          if (!params.get('generationId')) {
            params.set('generationId', generation.id);
            params.set('mode', mode || 'view');
            navigate(`${location.pathname}?${params.toString()}`, { replace: true });
          }
          
          // Track generation event
          trackEvent.mutate({
            generationId: generation.id,
            eventType: 'generate',
            eventData: { questionCount: questions.length, configType: 'custom' }
          });
        } catch (error) {
          console.error('Error creating generation:', error);
          toast({
            title: t('results.saveFailedTitle'),
            description: t('results.saveFailedDesc'),
            variant: "destructive",
          });
        }
      } else if (!user && tool?.id) {
        try {
          const { data, error } = await supabase.functions.invoke('create-generation', {
            body: {
              payload: {
                tool_id: tool.id,
                email: (config as any).email || undefined,
                title: `${config.industry} Survey - ${config.companySize}`,
                generation_type: 'survey',
                generated_content: { questions },
                configuration: config,
                estimated_duration_minutes: Math.ceil(questions.length * 1.5),
                status: 'active',
              },
            },
          });

          if (error) throw error;

          const generation = (data as any)?.generation;
          if (generation) {
            setCurrentGeneration(generation);
            // Append generationId in URL for refresh persistence
            const params = new URLSearchParams(location.search);
            if (!params.get('generationId')) {
              params.set('generationId', generation.id);
              params.set('mode', mode || 'view');
              navigate(`${location.pathname}?${params.toString()}`, { replace: true });
            }
            trackEvent.mutate({
              generationId: generation.id,
              eventType: 'generate',
              eventData: { questionCount: questions.length, configType: 'custom', anonymous: true },
            });
          }
        } catch (err) {
          console.error('Anonymous generation save failed:', err);
          // Still allow proceeding without saving
        }
      }
      
    toast({
      title: t('results.generatedTitle'),
      description: t('results.generatedDesc', { count: questions.length }),
    });
    } catch (error) {
      console.error('Failed to generate survey:', error);
    toast({
      title: t('results.failedTitle'),
      description: t('results.failedDesc'),
      variant: "destructive",
    });
      setHasGenerated(false); // Allow retry
    } finally {
      setIsGenerating(false);
    }
  };

  const handleAnswerChange = (questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleQuestionChange = (index: number, field: string, value: any) => {
    const updatedQuestions = [...generatedQuestions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setGeneratedQuestions(updatedQuestions);
    setHasChanges(true);
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `custom_${Date.now()}`,
      question: '',
      type: 'text',
      category: 'custom',
      required: true,
      options: []
    };
    setGeneratedQuestions([...generatedQuestions, newQuestion]);
    setHasChanges(true);
  };

  const handleDeleteQuestion = (index: number) => {
    setGeneratedQuestions(generatedQuestions.filter((_, i) => i !== index));
    setHasChanges(true);
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;

    const items = Array.from(generatedQuestions);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setGeneratedQuestions(items);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!currentGeneration) return;
    
    try {
      await updateGeneration.mutateAsync({
        id: currentGeneration.id,
        updates: {
          title,
          generated_content: { questions: generatedQuestions },
          updated_at: new Date().toISOString()
        }
      });
      setHasChanges(false);
      toast({
        title: t('results.saveSuccessTitle'),
        description: t('results.saveSuccessDesc'),
      });
    } catch (error) {
      console.error('Error saving generation:', error);
      toast({
        title: t('results.saveFailedTitle'),
        description: t('results.saveFailedDesc'),
        variant: "destructive",
      });
    }
  };

  const copyAllQuestions = async () => {
    const questionsText = generatedQuestions
      .map((question, index) => `${index + 1}. ${question.question}`)
      .join('\n\n');
    
    try {
      await navigator.clipboard.writeText(questionsText);
      toast({
        title: "Questions Copied",
        description: "All survey questions have been copied to your clipboard.",
      });
    } catch (error) {
      toast({
        title: t('results.copyFailedTitle'),
        description: t('results.copyFailedDesc'),
        variant: "destructive",
      });
    }
  };

  const exportQuestions = (format: 'json' | 'csv') => {
    if (format === 'json') {
      const exportData = {
        surveyConfig: config,
        questions: generatedQuestions,
        metadata: {
          generatedAt: new Date().toISOString(),
          estimatedCompletionTime: `${getEstimatedTime()} minutes`,
          questionBreakdown: getQuestionBreakdown()
        }
      };
      
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee-survey-${config.industry}-${Date.now()}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      // CSV format
      const csvHeader = 'Question Number,Category,Question Type,Question Text,Options\n';
      const csvContent = generatedQuestions
        .map((question, index) => {
          const options = question.options ? question.options.join('; ') : '';
          return `${index + 1},"${question.category || question.type}","${question.type}","${question.question.replace(/"/g, '""')}","${options}"`;
        })
        .join('\n');
      
      const blob = new Blob([csvHeader + csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `employee-survey-${config.industry}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
    
  toast({
    title: t('results.exportCompleteTitle'),
    description: t('results.exportCompleteDesc', { format: format.toUpperCase() }),
  });
  };

  const getQuestionBreakdown = () => {
    const breakdown: Record<string, number> = {};
    generatedQuestions.forEach(q => {
      const category = q.category || q.type || 'General';
      breakdown[category] = (breakdown[category] || 0) + 1;
    });
    return breakdown;
  };

  const getEstimatedTime = () => {
    return Math.ceil(generatedQuestions.length * 0.75);
  };

  // If this is a conversation template result, render it differently
  if (templateId && conversationTemplate) {
    return (
      <AppLayout>
        <AppHeader
          title="Conversation Template Generated"
          subtitle="Your customized conversation template has been created successfully. Review the stages, questions, and objectives below."
            breadcrumbs={[
              { label: tool?.name || 'Tool', href: `/${currentLang}/tools/${tool?.slug || tool?.id}` },
              { label: 'Configuration', href: `/${currentLang}/tools/${tool?.slug || tool?.id}/configuration` },
              { label: 'Template Results' }
            ]}
        />
        
        <ConversationTemplateResults
          conversationTemplate={conversationTemplate}
          toolId={tool?.id}
          currentLang={currentLang}
          config={config}
          generatedQuestions={generatedQuestions}
        />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <AppHeader
        title={surveyTemplateId && surveyTemplate ? surveyTemplate.name : (tool?.name || 'Survey Tool')}
        subtitle={
          surveyTemplateId 
            ? `${surveyTemplate?.description || t('results.header.surveyTemplateFallbackSubtitle')}` 
            : `${t('results.header.generationFallbackSubtitle')}`
        }
            breadcrumbs={
              surveyTemplateId 
                ? [
                    { label: t('results.header.breadcrumbs.surveyTemplates'), href: `/${currentLang}/survey-templates` },
                    { label: surveyTemplate?.name || t('results.header.breadcrumbs.template'), href: `/${currentLang}/survey-templates/${surveyTemplateId}` },
                    { label: t('results.header.breadcrumbs.results') }
                  ]
                 : [
                     { label: tool?.name || t('results.header.breadcrumbs.tool'), href: `/${currentLang}/tools/${tool?.slug || tool?.id}` },
                     { label: t('results.header.breadcrumbs.configuration'), href: `/${currentLang}/tools/${tool?.slug || tool?.id}/configuration` },
                     { label: t('results.header.breadcrumbs.results') }
                   ]
            }
      />

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12">
        <div className="max-w-7xl mx-auto">
          {(isGenerating || shouldAutoGenerate) ? (
            <LoadingState />
          ) : generatedQuestions.length > 0 ? (
            <SurveyMainContent
              generatedQuestions={generatedQuestions}
              answers={answers}
              mode={mode}
              currentGeneration={currentGeneration}
              user={user}
              toolId={tool?.slug || toolParam || tool?.id}
              currentLang={currentLang}
              surveyTemplateId={surveyTemplateId}
              surveyTemplate={surveyTemplate}
              config={config}
              isGenerating={isGenerating}
              onQuestionChange={handleQuestionChange}
              onAnswerChange={handleAnswerChange}
              onAddQuestion={handleAddQuestion}
              onDeleteQuestion={handleDeleteQuestion}
              onDragEnd={handleDragEnd}
              onSave={handleSave}
              isSaving={updateGeneration.isPending}
              hasChanges={hasChanges}
              onGenerateQuestions={generateQuestions}
              onCopyAllQuestions={copyAllQuestions}
              onExportQuestions={exportQuestions}
              onShowContactDialog={() => setShowContactDialog(true)}
            />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>

      {/* Contact Jigso Dialog */}
      <ContactDialog
        open={showContactDialog}
        onOpenChange={setShowContactDialog}
        config={config}
        generatedQuestions={generatedQuestions}
        dialogType="survey"
      />
      
      <Footer />
    </AppLayout>
  );
};

export default ToolResults;